import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { Unit, UnitCreate } from '../types';
import { unitsAPI } from '../services/api';
import { useUnitsList, useDeletedUnits, useInvalidateUnits } from '../hooks/queries/useUnits';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import CustomTablePagination from './CustomTablePagination';
import PermissionButton from './PermissionButton';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';

const CONFIG_COLOR = '#1976D2';

const UNIT_FILTER_SCHEMA = {
  name:       { type: 'text', param: 'name' },
  created_at: { type: 'dateRange', paramFrom: 'created_from', paramTo: 'created_to' },
} as const;

const UnitManagement: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [formData, setFormData] = useState({ name: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { filters, update, reset, hasActive } = useColumnFilters(UNIT_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  // Cached list + deleted list; mutations invalidate to refetch.
  const unitsQuery = useUnitsList();
  const deletedQuery = useDeletedUnits(tabValue === 1);
  const invalidate = useInvalidateUnits();
  const units = unitsQuery.data ?? [];
  const deletedUnits = deletedQuery.data ?? [];
  const loading = tabValue === 0 ? unitsQuery.isInitialLoading : deletedQuery.isInitialLoading;

  const handleRestore = async (unit: Unit) => {
    try {
      await unitsAPI.restore(unit.id);
      showSuccess('Unité restaurée avec succès');
      invalidate();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Erreur lors de la restauration');
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;

    try {
      setFormLoading(true);
      
      if (selectedUnit) {
        await unitsAPI.update(selectedUnit.id, { name: formData.name });
        showSuccess('Unité modifiée avec succès');
      } else {
        await unitsAPI.create({ name: formData.name });
        showSuccess('Unité créée avec succès');
      }
      
      setIsFormOpen(false);
      setSelectedUnit(null);
      setFormData({ name: '' });
      invalidate();
    } catch (err: any) {
      showError(
        err.response?.data?.detail || 
        'Erreur lors de la sauvegarde de l\'unité'
      );
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (unit: Unit) => {
    try {
      await unitsAPI.delete(unit.id);
      invalidate();
      showSuccess('Unité supprimée avec succès');
    } catch (err: any) {
      showError(
        err.response?.data?.detail || 
        'Erreur lors de la suppression de l\'unité'
      );
    }
  };

  const openForm = (unit?: Unit) => {
    setSelectedUnit(unit || null);
    setFormData({ name: unit?.name || '' });
    setIsFormOpen(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredUnits = useMemo(
    () =>
      units.filter((u) => {
        if (filters.name && !u.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
        if (filters.created_at.from && new Date(u.created_at) < new Date(filters.created_at.from)) return false;
        if (filters.created_at.to && new Date(u.created_at) > new Date(`${filters.created_at.to}T23:59:59`)) return false;
        return true;
      }),
    [units, filters]
  );

  // Get current page data based on tab
  const currentData = tabValue === 0 ? filteredUnits : deletedUnits;
  const paginatedUnits = currentData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box p={3} sx={{ width: '100%', maxWidth: 'none' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Gestion des Unités
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
          <PermissionButton
            moduleName="Config"
            operation="add"
            miniModuleName="Units"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openForm()}
            sx={{
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            Nouvelle Unité
          </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Unités Actives" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
      <>
      <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Nom
                  <ColumnFilter type="text" value={filters.name} onChange={(v) => update('name', v)} label="nom" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Date de Création
                  <ColumnFilter type="dateRange" value={filters.created_at} onChange={(v) => update('created_at', v)} label="date de création" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : filteredUnits.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  {hasActive() ? 'Aucune unité ne correspond aux filtres' : 'Aucune unité trouvée'}
                </TableCell>
              </TableRow>
            ) : (
              paginatedUnits.map((unit) => (
                <TableRow key={unit.id}>
                  <TableCell>{unit.name}</TableCell>
                  <TableCell>
                    {new Date(unit.created_at).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell align="center">
                    <PermissionButton
                      moduleName="Config"
                      operation="edit"
                      miniModuleName="Units"
                      component="IconButton"
                      onClick={() => openForm(unit)}
                    >
                      <EditIcon />
                    </PermissionButton>
                    <PermissionButton
                      moduleName="Config"
                      operation="delete"
                      miniModuleName="Units"
                      component="IconButton"
                      onClick={() => {
                        showConfirm(
                          `Êtes-vous sûr de vouloir supprimer l'unité "${unit.name}" ?`,
                          () => handleDelete(unit),
                          {
                            title: 'Confirmer la suppression',
                            confirmText: 'Supprimer',
                            cancelText: 'Annuler'
                          }
                        );
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </PermissionButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={filteredUnits.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {tabValue === 1 && (
      <>
      <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Date de Suppression</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : deletedUnits.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Aucune unité supprimée
                </TableCell>
              </TableRow>
            ) : (
              paginatedUnits.map((unit) => (
                <TableRow key={unit.id}>
                  <TableCell>{unit.name}</TableCell>
                  <TableCell>
                    {unit.deleted_at ? new Date(unit.deleted_at).toLocaleDateString('fr-FR') : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => {
                        showConfirm(
                          `Êtes-vous sûr de vouloir restaurer l'unité "${unit.name}" ?`,
                          () => handleRestore(unit),
                          {
                            title: 'Confirmer la restauration',
                            confirmText: 'Restaurer',
                            cancelText: 'Annuler'
                          }
                        );
                      }}
                      color="primary"
                      title="Restaurer"
                    >
                      <RestoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={deletedUnits.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)}>
        <DialogTitle>
          {selectedUnit ? 'Modifier l\'unité' : 'Nouvelle unité'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom de l'unité"
            fullWidth
            variant="filled"
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
            sx={{ mt: 2 }}
            placeholder="ex: KG, M², UNITE, ML"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormOpen(false)} disabled={formLoading}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formLoading || !formData.name.trim()}
            sx={{ 
              backgroundColor: '#4CAF50',
              '&:hover': {
                backgroundColor: '#388E3C',
              }
            }}
          >
            {formLoading ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default UnitManagement;